"use client";

// ── The statement half of the graded workspace (`EX-PUB01`/`EX-PUB08`) ───────
// One panel for the practice library and the contest, because the bilingual
// toggle is the product's differentiator (`docs/29` §5, `EX-PUB02`) and a
// second copy of it is a second place for Bangla to quietly stop rendering.
//
// What differs between the two callers is passed in, never branched on here:
// practice leads with a solved mark and a difficulty chip, a contest leads with
// its letter and reveals an editorial once it has ended. The panel owns the
// chosen language and hands it to `renderFooter`, so the editorial follows the
// toggle the reader just used.

import { useState, type ReactNode } from "react";
import { Check, Copy, Languages } from "lucide-react";
import { useTranslations } from "next-intl";

import { Button } from "@/components/ui/button";
import { RichContent } from "@/components/rich-content";
import { hasHtmlContent } from "@/lib/html-content";
import { formatMemoryLimitMb, formatTimeLimit } from "../../lib/format";
import { RunTwiceTag } from "../RunTwiceTag";
import { RunTwiceExplainer } from "./RunTwiceExplainer";
import { InteractiveTag } from "../InteractiveTag";
import { InteractiveExplainer, SampleConversation } from "./InteractiveExplainer";
import type { InteractionLine } from "@/features/judge-authoring/types";
import { hasFileIo, type ProblemFileIo } from "../../lib/file-io";
import { FileIoNotice, FileIoTag } from "./FileIoNotice";

export type StatementSample = {
  input: string;
  output: string;
  explanation_en: string | null;
  explanation_bn: string | null;
  /** `EX-RTW02`: a run-twice sample's example lines; absent on every other sample. */
  firstOutput?: string | null;
  secondInput?: string | null;
  /** `EX-IAC01`: an interactive sample's conversation; absent on every other sample. */
  interaction?: InteractionLine[];
};

export type StatementBody = {
  title_en: string;
  title_bn: string | null;
  statement_en: string;
  statement_bn: string | null;
  inputFormat_en: string | null;
  inputFormat_bn: string | null;
  outputFormat_en: string | null;
  outputFormat_bn: string | null;
  constraints_en: string | null;
  constraints_bn: string | null;
  notes_en: string | null;
  notes_bn: string | null;
  samples: StatementSample[];
  limits: { timeMs: number; memoryKb: number };
  /**
   * `EX-RTW02`: `run_twice` draws each sample as two rows, run 1 and run 2.
   * Absent on every caller that predates it, whose samples are unchanged.
   */
  kind?: "standard" | "run_twice" | "interactive";
  /** `EX-RTW05`: where the program reads and writes, when not stdin and stdout. Absent on every other caller. */
  fileIo?: ProblemFileIo;
};

/** `EX-STP01`: which samples keep their Copy button; a contest's copy guard narrows it. */
export type StatementSampleCopy = "all" | "input" | "none";

/** `EX-STP01`: the places inside the statement a caller may add to (the contest's decoy). */
export type StatementInterleaveSlot = "beforeStatement" | "afterStatement" | "afterSamples";

function SamplePre({ label, text, copyable = true }: { label: string; text: string; copyable?: boolean }) {
  const t = useTranslations("judge.problem");
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard refused - the text is still selectable by hand.
    }
  };

  return (
    <div>
      <div className="mb-1 flex items-center justify-between">
        <p className="text-xs font-semibold uppercase tracking-[0.06em] text-[rgb(var(--muted-foreground))]">
          {label}
        </p>
        {copyable ? (
          <Button variant="tertiary"
            type="button"
            onClick={() => void copy()}
            aria-label={t("copySample")}
            className="inline-flex min-h-11 items-center gap-2 rounded-lg px-3 py-2 text-xs font-medium text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-neutral-600"
          >
            {copied ? <Check className="h-3 w-3 text-emerald-600" /> : <Copy className="h-3 w-3" />}
            {copied ? t("copied") : t("copy")}
          </Button>
        ) : (
          // Keeps the row's height, so the two columns still line up.
          <span aria-hidden className="min-h-11" />
        )}
      </div>
      <pre className="max-h-40 overflow-auto rounded-lg border border-[rgb(var(--border))] bg-neutral-50 p-4 text-sm leading-relaxed text-neutral-800 whitespace-pre-wrap">
        {text}
      </pre>
    </div>
  );
}

/**
 * `EX-RTW02`: one sample of a run-twice problem, the four boxes of board C.
 * Run 1's input and run 2's output are the test; the two middle boxes are an
 * example only (a contestant's string may differ), and say so.
 */
function RunTwiceSample({
  sample,
  index,
  sampleCopy,
}: {
  sample: StatementSample;
  index: number;
  sampleCopy: StatementSampleCopy;
}) {
  const t = useTranslations("judge.problem");
  const rows = [
    {
      key: "first",
      label: t("sampleRun1"),
      input: { text: sample.input, example: false, copyable: sampleCopy !== "none" },
      output: { text: sample.firstOutput ?? "", example: true, copyable: sampleCopy === "all" },
    },
    {
      key: "second",
      label: t("sampleRun2"),
      input: { text: sample.secondInput ?? "", example: true, copyable: sampleCopy === "all" },
      output: { text: sample.output, example: false, copyable: sampleCopy === "all" },
    },
  ];
  return (
    <div className="overflow-hidden rounded-xl border border-[rgb(var(--border))]" data-testid="run-twice-sample">
      <p className="border-b border-[rgb(var(--border))] bg-neutral-50 px-3 py-2 text-xs font-bold text-neutral-800">
        {t("sampleN", { idx: index + 1 })}
      </p>
      {rows.map((row) => (
        <div key={row.key} className="grid gap-3 border-b border-neutral-100 p-3 last:border-b-0 sm:grid-cols-[96px_minmax(0,1fr)_minmax(0,1fr)]">
          <p className="text-xs font-bold text-[#C93D12] sm:pt-3">{row.label}</p>
          {[
            { cell: row.input, label: t("sampleRunInput") },
            { cell: row.output, label: t("sampleRunOutput") },
          ].map(({ cell, label }) => (
            <SamplePre
              key={label}
              label={cell.example ? `${label} · ${t("sampleExample")}` : label}
              text={cell.text}
              copyable={cell.copyable}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

export function StatementSection({ title, html }: { title: string; html: string | null }) {
  // An emptied section prints nothing, not a heading over a blank.
  if (!hasHtmlContent(html)) return null;
  return (
    <div className="mt-4">
      <p className="text-xs font-semibold uppercase tracking-[0.06em] text-[rgb(var(--muted-foreground))]">
        {title}
      </p>
      <div className="mt-2 text-sm leading-7 text-neutral-700">
        <RichContent html={html} />
      </div>
    </div>
  );
}

export function ProblemStatementPanel({
  problem,
  locale,
  leading,
  meta,
  metaTrailing,
  renderFooter,
  lang,
  onLangChange,
  bare = false,
  underlay,
  sampleCopy = "all",
  interleave,
}: {
  problem: StatementBody;
  locale: string;
  /** Before the title: the solved mark on practice, the letter in a contest. */
  leading?: ReactNode;
  /** First chip on the meta row - difficulty on practice, nothing in a contest. */
  meta?: ReactNode;
  /** After the limits - tags on practice. */
  metaTrailing?: ReactNode;
  /** Below the statement, in the language the reader has chosen. */
  renderFooter?: (lang: "en" | "bn") => ReactNode;
  /**
   * `EX-PUB21`: the practice page lifts the title, the meta row and the language
   * toggle into its head strip, so it owns the language (`lang` with
   * `onLangChange`) and asks for the body alone (`bare`). The contest workspace
   * passes none of the three and renders exactly as before.
   */
  lang?: "en" | "bn";
  onLangChange?: (lang: "en" | "bn") => void;
  bare?: boolean;
  /** Decorative background below selectable statement content. */
  underlay?: ReactNode;
  /** `EX-STP01`: which samples keep their Copy button. Every other caller keeps `all`. */
  sampleCopy?: StatementSampleCopy;
  /** `EX-STP01`: what a caller adds at fixed places inside the statement. */
  interleave?: (slot: StatementInterleaveSlot) => ReactNode;
}) {
  const t = useTranslations("judge.problem");
  const tTwice = useTranslations("judge.runTwice");
  const tInteractive = useTranslations("judge.interactive");
  const runTwice = problem.kind === "run_twice";
  // `EX-IAC01`: the tag, the box, the Interaction heading and the conversations.
  const interactive = problem.kind === "interactive";
  const fileIo = hasFileIo(problem.fileIo) ? problem.fileIo : null;
  const [ownLang, setOwnLang] = useState<"en" | "bn">(locale === "bn" ? "bn" : "en");
  const statementLang = lang ?? ownLang;
  const setStatementLang = onLangChange ?? setOwnLang;

  const useBn = statementLang === "bn";
  // A Bangla field with no content (an emptied editor's `<p></p>`) is no Bangla: the reader gets English.
  const hasBn = hasHtmlContent(problem.statement_bn) || !!problem.title_bn;
  const title = useBn && problem.title_bn ? problem.title_bn : problem.title_en;
  const statementHtml =
    useBn && hasHtmlContent(problem.statement_bn) ? problem.statement_bn : problem.statement_en;

  const content = (
    <>
      {bare ? null : (
        <>
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div className="flex min-w-0 items-center gap-2">
              {leading}
              <h1 className="text-2xl font-bold tracking-tight text-neutral-900">{title}</h1>
            </div>
            {hasBn ? (
              <Button variant="tertiary"
                type="button"
                onClick={() => setStatementLang(statementLang === "en" ? "bn" : "en")}
                className="flex min-h-11 items-center gap-2 rounded-lg border border-[rgb(var(--border))] bg-white px-2.5 py-1 text-xs font-medium text-neutral-600 transition-colors hover:bg-neutral-50"
              >
                <Languages className="h-3.5 w-3.5" />
                {statementLang === "en" ? "বাংলা" : "English"}
              </Button>
            ) : null}
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-2 text-xs">
            {meta}
            {runTwice ? <RunTwiceTag /> : null}
            {interactive ? <InteractiveTag /> : null}
            {fileIo ? <FileIoTag fileIo={fileIo} /> : null}
            <span className="text-[rgb(var(--muted-foreground))]">
              {runTwice
                ? tTwice("timePerRun", { seconds: formatTimeLimit(problem.limits.timeMs) })
                : t("timeLimitValue", { seconds: formatTimeLimit(problem.limits.timeMs) })}
            </span>
            <span className="text-neutral-300">·</span>
            <span className="text-[rgb(var(--muted-foreground))]">
              {t("memoryLimitValue", { mb: formatMemoryLimitMb(problem.limits.memoryKb) })}
            </span>
            {metaTrailing}
          </div>
        </>
      )}

      {/* `EX-RTW03`, board D: before the statement, so it is read first. */}
      {runTwice ? <div className={bare ? "mb-5" : "mt-6"}><RunTwiceExplainer /></div> : null}
      {interactive ? <div className={bare ? "mb-5" : "mt-6"}><InteractiveExplainer /></div> : null}
      {/* `EX-RTW05`: before the statement too, so where the input comes from is read first. */}
      {fileIo ? <div className={bare ? "mb-5" : runTwice ? "mt-3" : "mt-6"}><FileIoNotice fileIo={fileIo} /></div> : null}
      {interleave?.("beforeStatement")}
      <div
        lang={statementLang}
        className={bare ? "text-base leading-7 text-neutral-800" : "mt-6 text-base leading-7 text-neutral-800"}
      >
        <RichContent html={statementHtml} />
      </div>
      {interleave?.("afterStatement")}

      <StatementSection
        title={t("inputFormat")}
        html={useBn ? problem.inputFormat_bn : problem.inputFormat_en}
      />
      <StatementSection
        title={interactive ? tInteractive("interactionHeading") : t("outputFormat")}
        html={useBn ? problem.outputFormat_bn : problem.outputFormat_en}
      />
      <StatementSection
        title={t("constraints")}
        html={useBn ? problem.constraints_bn : problem.constraints_en}
      />

      {problem.samples.length > 0 ? (
        <div className="mt-5 space-y-4">
          {problem.samples.map((sample, index) => {
            const explanation = useBn ? sample.explanation_bn : sample.explanation_en;
            if (interactive) {
              return (
                <div key={index} className="space-y-2">
                  <SampleConversation lines={sample.interaction ?? []} index={index} />
                  {explanation ? <p className="text-xs text-[rgb(var(--muted-foreground))]">{explanation}</p> : null}
                </div>
              );
            }
            if (problem.kind === "run_twice") {
              return (
                <div key={index} className="space-y-2">
                  <RunTwiceSample sample={sample} index={index} sampleCopy={sampleCopy} />
                  {explanation ? <p className="text-xs text-[rgb(var(--muted-foreground))]">{explanation}</p> : null}
                </div>
              );
            }
            return (
              <div key={index} className="grid gap-4 sm:grid-cols-2">
                <SamplePre
                  label={t("sampleInput", { idx: index + 1 })}
                  text={sample.input}
                  copyable={sampleCopy !== "none"}
                />
                <SamplePre
                  label={t("sampleOutput", { idx: index + 1 })}
                  text={sample.output}
                  copyable={sampleCopy === "all"}
                />
                {explanation ? (
                  <p className="text-xs text-[rgb(var(--muted-foreground))] sm:col-span-2">
                    {explanation}
                  </p>
                ) : null}
              </div>
            );
          })}
        </div>
      ) : null}
      {interleave?.("afterSamples")}

      <StatementSection title={t("notes")} html={useBn ? problem.notes_bn : problem.notes_en} />

      {renderFooter?.(statementLang)}
    </>
  );
  const panelClass = bare ? undefined : "rounded-xl border border-neutral-200 bg-white p-4 sm:p-6";
  return (
    <div className={underlay ? `relative isolate ${panelClass ?? ""}` : panelClass}>
      {underlay ? <>{underlay}<div className="relative z-[1]">{content}</div></> : content}
    </div>
  );
}
